import { z } from 'zod';

export const PLATFORM_IDS = ['wix', 'etsy', 'ebay', 'amazon', 'shopify', 'woocommerce'] as const;
export const PlatformIdSchema = z.enum(PLATFORM_IDS);
export type PlatformId = z.infer<typeof PlatformIdSchema>;

export const PRODUCT_STATUSES = ['active', 'draft', 'archived'] as const;
export const ProductStatusSchema = z.enum(PRODUCT_STATUSES);
export type ProductStatus = z.infer<typeof ProductStatusSchema>;

/**
 * Universal product shape the Studio core sees, regardless of source platform.
 * Zod schema is the source of truth; the TypeScript type is derived from it.
 */
export const UniversalProductSchema = z.object({
  /** Global identifier: `${platformId}:${platformProductId}`. */
  uid: z.string(),
  platformId: PlatformIdSchema,
  platformProductId: z.string(),
  title: z.string(),
  sku: z.string().nullable(),
  status: ProductStatusSchema,
  url: z.string().nullable(),
  primaryImageUrl: z.string().nullable(),
  imageCount: z.number().int(),
  variantCount: z.number().int(),
  /** ISO 8601 timestamp. */
  lastSyncedAt: z.string(),
});

export type UniversalProduct = z.infer<typeof UniversalProductSchema>;

/**
 * Universal media shape — one image attached to one platform product.
 */
export interface UniversalMedia {
  /** `${platformId}:${platformProductId}:${platformMediaId}`. */
  uid: string;
  platformId: PlatformId;
  platformProductId: string;
  platformMediaId: string;
  url: string;
  width: number | null;
  height: number | null;
  position: number;
  isPrimary: boolean;
  altText: string | null;
}

/**
 * Draft image — generated locally, not yet attached to a platform product.
 */
export interface DraftMedia {
  id: string;
  workspaceId: string;
  productUid: string | null;
  /** Signed URL on GCS. */
  url: string;
  width: number;
  height: number;
  /** ISO 8601. */
  createdAt: string;
}

export type GalleryImageKind = 'live' | 'draft' | 'sales';

export type GalleryImage =
  | (UniversalMedia & { kind: 'live' | 'sales'; productTitle: string | null })
  | (DraftMedia & { kind: 'draft'; productTitle: string | null });

export interface ListProductsOptions {
  query?: string;
  status?: ProductStatus;
  limit?: number;
  cursor?: string | null;
}

export interface AttachOptions {
  asPrimary?: boolean;
  position?: number;
}

export interface MediaUploadInput {
  /** GCS-hosted URL of the image to upload. */
  fileUrl: string;
  altText?: string;
  position?: number;
}
